import { useState } from 'react';
import UserDataService from '../services/user.service';
import { useUsers } from '../hooks/useUsers';
import UserForm from '../components/UserForm';
import UserCard from '../components/UserCard';
import StatsCard from '../components/StatsCard';
import { auth } from '../firebase/firebase-config';
import { signOut } from 'firebase/auth';
import hanapLogo from '../assets/logo.png';

const Home = () => {
  const { users } = useUsers();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("All"); // <-- Replaced sortBy with activeFilter
  const [showAddForm, setShowAddForm] = useState(false);
  
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const createUser = async (newUserData) => {
    if (!auth.currentUser) return alert("You must be logged in to add members!");
    
    const formattedRoles = newUserData.roles ? newUserData.roles.map(role => ({
        duty: role.duty,
        swore_date: role.swore_date || ""
    })) : [];

    const newUser = { 
      ...newUserData,
      roles: formattedRoles,
      owner_uid: auth.currentUser.uid
    };
    
    await UserDataService.addUser(newUser);
    setShowAddForm(false);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    await UserDataService.updateUser(id, { status: newStatus });
  };

  const handleAddRole = async (userId, duty, date) => {
    const targetUser = users.find(u => u.id === userId);
    if (targetUser?.roles?.some(r => r.duty === duty)) {
        return alert(`This user is already a "${duty}"!`);
    }
    const newRole = { duty: duty, swore_date: date || "" };
    await UserDataService.addRoleToUser(userId, newRole);
  };

  const handleEditUser = async (userId, updatedFields) => {
    const safeUpdates = { ...updatedFields };

    if (safeUpdates.roles) {
        safeUpdates.roles = safeUpdates.roles.map(role => ({
            ...role,
            swore_date: role.swore_date || ""
        }));
    }
    await UserDataService.updateUser(userId, safeUpdates);
  };

  const handleDeleteRole = async (userId, roleToDelete) => {
    if(window.confirm(`Remove role "${roleToDelete.duty}"?`)) {
      await UserDataService.removeRoleFromUser(userId, roleToDelete);
    }
  };

  const deleteUser = async (id) => {
    if(window.confirm("Are you sure you want to delete this user?")) {
        await UserDataService.deleteUser(id);
    }
  };

  // HELPER: To get valid roles excluding "None"
  const getValidRoles = (user) => {
    if (!user.roles) return [];
    return user.roles.filter(r => r.duty && r.duty.toLowerCase() !== 'none');
  };

  // Master Filter & Search Logic
  const processedUsers = users.filter((user) => {
    // 1. Apply Active Filter from StatsCard
    let matchesFilter = true;
    if (activeFilter === 'Active') {
      matchesFilter = getValidRoles(user).length > 0 && (!user.status || user.status === 'Active');
    } else if (activeFilter === 'Inactive') {
      matchesFilter = getValidRoles(user).length > 0 && user.status === 'Inactive';
    } else if (['Buklod', 'Kadiwa', 'Binhi'].includes(activeFilter)) {
      matchesFilter = user.category === activeFilter;
    } else if (activeFilter !== 'All' && activeFilter !== 'Officers') {
      // Must be a specific Role string
      matchesFilter = getValidRoles(user).some(r => r.duty.toUpperCase() === activeFilter);
    } else if (activeFilter === 'Officers') {
      matchesFilter = getValidRoles(user).length > 0;
    }

    if (!matchesFilter) return false;

    // 2. Apply Search Term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      const matchesUnangPangalan = user.unangPangalan?.toLowerCase().includes(lowerSearch);
      const matchesApelyidoIna = user.apelyidoSaIna?.toLowerCase().includes(lowerSearch);
      const matchesApelyidoAma = user.apelyidoSaAma?.toLowerCase().includes(lowerSearch);
      const matchesApelyidoAsawa = user.apelyidoSaAsawa?.toLowerCase().includes(lowerSearch);
      const matchesCategory = user.category?.toLowerCase().includes(lowerSearch);
      const matchesReg = user.registry_number?.toLowerCase().includes(lowerSearch);
      const matchesControl = user.controlNumber?.toLowerCase().includes(lowerSearch);
      const matchesPrk = user.prkGrp?.toLowerCase().includes(lowerSearch);
      const matchesHanapbuhay = user.hanapbuhay?.toLowerCase().includes(lowerSearch);
      const matchesEdukasyon = user.edukasyon?.toLowerCase().includes(lowerSearch);
      const matchesRole = user.roles && user.roles.some(r => r.duty.toLowerCase().includes(lowerSearch));
      
      return matchesUnangPangalan || matchesApelyidoIna || matchesApelyidoAma || matchesApelyidoAsawa || 
             matchesCategory || matchesRole || matchesReg || matchesControl || matchesPrk || matchesHanapbuhay || matchesEdukasyon;
    }

    return true;
  }).sort((a, b) => {
    // Always sort alphabetically by name as default fallback
    const nameA = a.name || a.unangPangalan || "";
    const nameB = b.name || b.unangPangalan || "";
    return nameA.localeCompare(nameB);
  });

  return (
    <div className="App">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <img 
            src={hanapLogo} 
            alt="Hanap Logo" 
            style={{ width: '100px', height: '100px', objectFit: 'contain', borderRadius: '8px' }} 
        />
        <button onClick={handleLogout} className="btn btn-cancel" style={{ padding: '8px 16px', fontSize: '0.9rem' }}>
          Sign Out
        </button>
      </div>
      
      {/* Pass activeFilter state to StatsCard so it can highlight and update selections */}
      <StatsCard 
        users={users} 
        activeFilter={activeFilter} 
        setActiveFilter={setActiveFilter} 
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '600px', margin: '0 auto 2rem auto' }}>
        
        {/* Dynamic header showing current filter */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            {activeFilter === 'All' ? 'Showing All Members' : `Filtering by: ${activeFilter}`}
          </span>
          {activeFilter !== 'All' && (
            <button 
              onClick={() => setActiveFilter('All')} 
              style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Clear Filter
            </button>
          )}
        </div>

        <input 
          placeholder="🔍 Search Name, Role, Category, or Registry No..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '100%' }}
        />

        <button 
          className={showAddForm ? "btn btn-cancel" : "btn btn-primary"} 
          onClick={() => setShowAddForm(!showAddForm)}
          style={{ width: '100%' }}
        >
          {showAddForm ? '✕ Cancel Adding Member' : '+ Add New Member'}
        </button>
      </div>
      
      {showAddForm && (
        <div style={{ marginBottom: '2.5rem' }}>
          <UserForm onCreate={createUser} />
        </div>
      )}

      <div className="user-grid">
        {processedUsers.map((user) => (
          <UserCard 
            key={user.id} 
            user={user} 
            onAddRole={handleAddRole}
            onDelete={deleteUser}
            onDeleteRole={handleDeleteRole}
            onEditUser={handleEditUser}
            onToggleStatus={handleToggleStatus}
          />
        ))}
        {processedUsers.length === 0 && (
          <p style={{textAlign: 'center', gridColumn: '1/-1', color: 'var(--text-muted)'}}>
            No members found matching "{searchTerm}" {activeFilter !== 'All' ? `under ${activeFilter}` : ''}
          </p>
        )}
      </div>
    </div>
  );
};

export default Home;